import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useTranslation } from "react-i18next";
import { useState, useCallback, useMemo } from "react";
import Button from "common/components/Button/Button";
import useAvailableHeight from "common/utils/useAvailableHeight";
import { classNames } from "primereact/utils";
import BoldLabel from "./components/body/BoldLabel";
import Image from "./components/body/Image";
import ColorComponent from "./components/body/Color";
import IconComponent from "./components/body/Icon";
import PhoneNumber from "./components/body/PhoneNumber";
import FileLabel from "./components/body/FileLabel";

type TableColumnRenderType =
  | "bold"
  | "image"
  | "color"
  | "icon"
  | "phone"
  | "boolean"
  | "file";

export type TableColumn<T = any> = {
  field: keyof T | string;
  header: string;
  sortable?: boolean;
  body?: (rowData: T) => React.ReactNode;
  style?: React.CSSProperties;
  headerStyle?: React.CSSProperties;
  renderType?: TableColumnRenderType;
  customRender?: (rowData: T) => React.ReactNode;
  isActionColumn?: boolean;
};

export interface GenericDataTableProps<T> {
  data: T[];
  columns: TableColumn<T>[];

  // Sort (format: "field:ASC" or "field:DESC")
  sort?: string;
  setSort?: React.Dispatch<React.SetStateAction<string>>;

  // Actions
  onDelete?: (id: any) => Promise<any>;
  onNavigate?: (id: any) => void;
  onEdit?: (id: any) => void;
  onRowClick?: (rowData: T) => void;

  // Actions column config
  showActions?: boolean;
  actionsHeader?: string;
  actionsWidth?: string;

  // Row config
  rowKey?: keyof T | string;
  emptyMessage?: string;

  // Header config
  header?: React.ReactNode;

  // Selection
  selectionMode?: "checkbox" | "single" | "multiple";
  selection?: T[];
  onSelectionChange?: (selection: T[]) => void;

  // Row expansion
  rowExpansionTemplate?: (rowData: T) => React.ReactNode;
  rowExpandable?: (rowData: T) => boolean;

  // Style
  tableStyle?: React.CSSProperties;
  className?: string;
}

function renderBody(rowData: any, column: TableColumn) {
  const { renderType, customRender } = column;

  if (!renderType && !customRender) {
    return <span>{rowData[column.field as keyof typeof rowData] || "-"}</span>;
  }

  switch (renderType) {
    case "bold":
      return (
        <BoldLabel
          label={rowData[column.field as keyof typeof rowData] || "-"}
        />
      );
    case "image":
      return <Image image={rowData[column.field as keyof typeof rowData]} />;
    case "color":
      return rowData[column.field as keyof typeof rowData] ? (
        <ColorComponent color={rowData[column.field as keyof typeof rowData]} />
      ) : (
        <span>-</span>
      );
    case "icon":
      return rowData[column.field as keyof typeof rowData] ? (
        <IconComponent
          iconName={rowData[column.field as keyof typeof rowData]}
        />
      ) : (
        <span>-</span>
      );
    case "phone":
      return rowData[column.field as keyof typeof rowData] ? (
        <PhoneNumber label={rowData[column.field as keyof typeof rowData]} />
      ) : (
        <span>-</span>
      );
    case "file":
      return <FileLabel file={rowData[column.field as keyof typeof rowData]} />;
    case "boolean":
      return (
        <span>
          {rowData[column.field as keyof typeof rowData] ? "✔️" : "❌"}
        </span>
      );
    default:
      if (customRender) {
        return customRender(rowData);
      }
      return (
        <span>{rowData[column.field as keyof typeof rowData] || "-"}</span>
      );
  }
}

function GenericDataTable<T extends Record<string, any>>({
  data,
  columns,
  sort,
  setSort,
  onDelete,
  onNavigate,
  onEdit,
  onRowClick,
  showActions = true,
  actionsHeader = "Actions",
  actionsWidth = "150px",
  rowKey = "id",
  emptyMessage,
  header,
  selectionMode,
  selection,
  onSelectionChange,
  rowExpansionTemplate,
  rowExpandable,
  tableStyle = { minWidth: "50rem" },
  className = "",
}: GenericDataTableProps<T>) {
  const { t } = useTranslation();
  const [deletingId, setDeletingId] = useState<any>(null);
  const [expandedRows, setExpandedRows] = useState<any[]>([]);
  const { ref, height } = useAvailableHeight(0);

  const handleDelete = useCallback(
    async (id: any) => {
      if (!onDelete) return;

      setDeletingId(id);
      try {
        await onDelete(id);
      } finally {
        setDeletingId(null);
      }
    },
    [onDelete]
  );

  const actionColumn = useMemo(
    () => columns.find((col) => col.isActionColumn),
    [columns]
  );

  // Template pour les actions
  const actionsBodyTemplate = useCallback(
    (rowData: T) => {
      const id = rowData[rowKey as keyof T];
      const isDeleting = deletingId === id;

      return (
        <div className="flex gap-2">
          {actionColumn?.customRender && actionColumn.customRender(rowData)}
          {onEdit && (
            <Button
              icon="PencilSquareIcon"
              type="neutral-line"
              compact
              onClick={() => onEdit(id)}
              stopPropagation
              disabled={isDeleting}
            />
          )}
          {onDelete && (
            <Button
              icon="TrashIcon"
              type="error-line"
              compact
              confirm
              confirmMessage={t("actions.confirmDelete")}
              onClick={() => handleDelete(id)}
              stopPropagation
              loading={isDeleting}
              disabled={isDeleting}
            />
          )}
        </div>
      );
    },
    [rowKey, deletingId, actionColumn, onEdit, onDelete, t, handleDelete]
  );

  // Parse le sort string (format: "field:ASC" ou "field:DESC") vers le format PrimeReact
  const { sortField, sortOrder } = useMemo(() => {
    if (!sort) {
      return { sortField: undefined, sortOrder: 0 };
    }
    const [field, order] = sort.split(":");
    return {
      sortField: field,
      sortOrder: order === "ASC" ? 1 : order === "DESC" ? -1 : 0,
    };
  }, [sort]);

  // Sort handler - convertit le format PrimeReact vers le format string
  const onSortChange = useCallback(
    (event: any) => {
      if (setSort) {
        const order = event.sortOrder === 1 ? "ASC" : "DESC";
        setSort(`${event.sortField}:${order}`);
      }
    },
    [setSort]
  );

  return (
    <div
      className={`generic-datatable rounded-lg overflow-hidden ring-2 ring-primary-100 bg-transparent ${className}`}
      ref={ref}
    >
      <DataTable
        value={data}
        tableStyle={tableStyle}
        emptyMessage={emptyMessage || t("noData") || "Aucune donnée disponible"}
        lazy
        dataKey={rowKey as string}
        stripedRows
        className="text-sm"
        sortField={sortField}
        sortOrder={sortOrder as any}
        onSort={onSortChange}
        selection={selection}
        onSelectionChange={(e: any) => onSelectionChange?.(e.value)}
        selectionMode={selectionMode as any}
        onRowClick={
          onRowClick
            ? (e) => onRowClick(e.data?.id)
            : onNavigate
            ? (e) => onNavigate(e.data?.id)
            : undefined
        }
        expandedRows={rowExpansionTemplate ? expandedRows : undefined}
        onRowToggle={
          rowExpansionTemplate ? (e: any) => setExpandedRows(e.data) : undefined
        }
        rowExpansionTemplate={rowExpansionTemplate as any}
        rowClassName={(rowData: any) => {
          const rowIndex = data.findIndex(
            (item) => item[rowKey as keyof T] === rowData[rowKey as keyof T]
          );
          const isSelected = selection?.some(
            (item) =>
              item[rowKey as keyof typeof item] ===
              rowData[rowKey as keyof typeof rowData]
          );
          return classNames(
            onRowClick || onNavigate
              ? "cursor-pointer hover:!bg-primary-50"
              : "",
            rowIndex % 2 === 1 ? "!bg-slate-50" : "!bg-white",
            isSelected
              ? "![box-shadow:inset_4px_0_0_#009FED] data-table-row-selected"
              : ""
          );
        }}
        scrollable
        scrollHeight={height ? `${height}px` : undefined}
        header={header}
        pt={
          {
            wrapper: {
              className: "bg-primary-100",
            },
            thead: {
              className: "rounded-t-lg bg-primary-100 overflow-hidden",
            },
            column: {
              headercell: ({ context: _context }: any) => ({
                className: classNames(
                  "text-left font-bold",
                  "transition duration-200",
                  "border-b-2 border-primary-100",
                  // "first:rounded-l-lg last:rounded-r-lg",
                  // context?.size === "small"
                  //   ? "p-2"
                  //   : context?.size === "large"
                  //   ? "p-5"
                  //   : "p-4", // Size
                  // context.sorted ?
                  "bg-primary-100 text-primary-700"
                  // : "bg-primary-100 text-slate-700" // Sort
                ),
              }),
            },
          } as any
        }
      >
        {selectionMode && (
          <Column
            selectionMode="multiple"
            headerStyle={{ width: "3rem" }}
            style={{ width: "3rem" }}
          />
        )}
        {rowExpansionTemplate && (
          <Column
            expander={rowExpandable ?? true}
            style={{ width: "3rem" }}
            headerStyle={{ width: "3rem" }}
          />
        )}
        {columns
          .filter((col) => !col.isActionColumn)
          .map((col) => (
            <Column
              key={String(col.field)}
              field={String(col.field)}
              header={col.header}
              sortable={col.sortable}
              body={(rowData) => renderBody(rowData, col)}
              // body={col.body}
              style={col.style}
              showFilterMenuOptions={false}
              showApplyButton={false}
              showClearButton={false}
              showFilterMatchModes={false}
              showFilterOperator={false}
              headerStyle={col.headerStyle}
            />
          ))}

        {showActions && (onDelete || onEdit || actionColumn) && (
          <Column
            body={actionsBodyTemplate}
            header={actionColumn?.header ?? actionsHeader}
            style={{ width: actionsWidth }}
            headerStyle={{ width: actionsWidth }}
          />
        )}
      </DataTable>
    </div>
  );
}

export default GenericDataTable;
